import {
  circularSpeed,
  defaultAscentProgram,
  degToRad,
  EARTH,
  GEO_ALTITUDE,
  isoToEpochSeconds,
  launchAzimuthCorrectedForRotation,
  radToDeg,
  type AscentProgram,
  type FlightOptions,
  type Geodetic,
  type MissionSimulationOptions,
  type WindProfile,
  type Vehicle,
} from '@trajectory/astro';
import type {
  AscentProgramDto,
  LaunchSiteDto,
  MissionDto,
  WeatherDto,
} from '@trajectory/contracts';
import { missionDestinationError } from '@trajectory/contracts';
import { toPhysicsVehicle } from './vehicle-adapter';

// Mirrors `ascentProgramFromDto`/`defaultProgramFor` in
// `apps/api/src/lib/mission-to-flight.ts` — see the note in
// `vehicle-adapter.ts` on why this is duplicated rather than shared.

export function ascentProgramFromDto(dto: AscentProgramDto): AscentProgram {
  return {
    azimuth: degToRad(dto.azimuthDeg),
    pitchStartAltitude: dto.pitchStartAltitudeMeters,
    turnEndAltitude: dto.turnEndAltitudeMeters,
    turnExponent: dto.turnExponent,
    targetApoapsisAltitude: dto.targetApoapsisAltitudeMeters,
    targetPeriapsisAltitude: dto.targetPeriapsisAltitudeMeters,
    closedLoopAltitude: dto.closedLoopAltitudeMeters,
    verticalSpeedTimeConstant: dto.verticalSpeedTimeConstantSeconds,
    maxAngleOfAttack: degToRad(dto.maxAngleOfAttackDeg),
    angleOfAttackQThreshold: dto.angleOfAttackQThresholdPa,
    maxQThrottleThreshold: dto.maxQThrottleThresholdPa,
    maxQThrottle: dto.maxQThrottle,
  };
}

function geodeticFromSite(site: LaunchSiteDto): Geodetic {
  return { latitude: degToRad(site.latitudeDeg), longitude: degToRad(site.longitudeDeg), altitude: site.elevationMeters };
}

/**
 * Build the ascent programme for a mission that did not specify one
 * explicitly: target the mission's stated orbit, picking the launch azimuth
 * that reaches its inclination (corrected for the planet's rotation), or
 * fall back to a 200 km due-east parking orbit when there is no target
 * orbit at all yet.
 */
function defaultProgramFor(mission: MissionDto, site: Geodetic): AscentProgram {
  if (!mission.targetOrbit) {
    const targetAltitude =
      mission.missionType === 'suborbital'
        ? 120_000
        : mission.missionType === 'meo'
          ? 20_200_000
          : mission.missionType === 'geo'
            ? GEO_ALTITUDE
            : 200_000;
    const program = defaultAscentProgram(targetAltitude, Math.PI / 2);
    return mission.missionType === 'suborbital'
      ? {
          ...program,
          targetApoapsisAltitude: 120_000,
          targetPeriapsisAltitude: -100_000,
        }
      : program;
  }

  const periapsisAltitude = Math.max(
    100_000,
    mission.targetOrbit.semiMajorAxisMeters * (1 - mission.targetOrbit.eccentricity) -
      EARTH.equatorialRadius,
  );
  const apoapsisAltitude = Math.max(
    periapsisAltitude,
    mission.targetOrbit.semiMajorAxisMeters * (1 + mission.targetOrbit.eccentricity) -
      EARTH.equatorialRadius,
  );
  const inclination = degToRad(mission.targetOrbit.inclinationDeg);
  const orbitalSpeed = circularSpeed(
    EARTH.equatorialRadius + periapsisAltitude,
    EARTH.mu,
  );
  const azimuth = launchAzimuthCorrectedForRotation(site.latitude, inclination, orbitalSpeed);

  if (azimuth === null) {
    throw new Error(
      `Launch site latitude ${radToDeg(site.latitude).toFixed(2)}° cannot reach a ${mission.targetOrbit.inclinationDeg}° inclination directly.`,
    );
  }
  return {
    ...defaultAscentProgram(apoapsisAltitude, azimuth),
    targetApoapsisAltitude: apoapsisAltitude,
    targetPeriapsisAltitude: periapsisAltitude,
  };
}

export interface BuildFlightOptionsInput {
  mission: MissionDto;
  vehicle: Vehicle;
  launchSite: LaunchSiteDto;
  maxDurationSeconds?: number;
}

/** Person, pressure suit, seat/restraints, and personal equipment allowance. */
export const CREW_SYSTEM_MASS_KG = 120;

export function windProfileFromWeather(weather: WeatherDto): WindProfile {
  const direction = degToRad(weather.surfaceWindDirectionDeg);
  const jetDirection = direction + degToRad(15);
  return {
    layers: [
      { altitude: 0, speed: weather.surfaceWindSpeed, direction },
      { altitude: 1000, speed: weather.surfaceWindSpeed * 1.25, direction },
      {
        altitude: Math.max(3000, weather.jetStreamAltitudeMeters * 0.5),
        speed: (weather.surfaceWindSpeed + weather.jetStreamSpeed) * 0.5,
        direction: jetDirection,
      },
      {
        altitude: weather.jetStreamAltitudeMeters,
        speed: weather.jetStreamSpeed,
        direction: jetDirection,
      },
      {
        altitude: Math.min(40_000, weather.jetStreamAltitudeMeters * 2),
        speed: weather.jetStreamSpeed * 0.35,
        direction: jetDirection,
      },
      { altitude: 80_000, speed: 0, direction: jetDirection },
    ],
    gustAmplitude: weather.gustAmplitude,
    gustWavelength: 900,
  };
}

/** Everything the Orbit Editor and Rocket Editor already hold, resolved into what `propagateFlight` needs. */
export function buildFlightOptions({ mission, vehicle, launchSite, maxDurationSeconds }: BuildFlightOptionsInput): FlightOptions {
  if (mission.weather?.lightningWithinConstraintRadius) {
    throw new Error(
      'Launch constraint violation: lightning is within the configured safety radius.',
    );
  }
  const site = mission.launchSiteOverride
    ? {
        latitude: degToRad(mission.launchSiteOverride.latitudeDeg),
        longitude: degToRad(mission.launchSiteOverride.longitudeDeg),
        altitude: mission.launchSiteOverride.altitudeMeters,
      }
    : geodeticFromSite(launchSite);

  const program = mission.ascentProgram ? ascentProgramFromDto(mission.ascentProgram) : defaultProgramFor(mission, site);
  const simulationHorizon =
    maxDurationSeconds ??
    (mission.missionType === 'suborbital'
      ? 7200
      : mission.missionType === 'meo'
        ? 12 * 3600
        : mission.missionType === 'geo'
          ? 24 * 3600
          : 1800);

  return {
    vehicle: {
      ...vehicle,
      payloadMass:
        mission.payloadMassKg +
        (mission.crewKind === 'crew' ? mission.crewCount * CREW_SYSTEM_MASS_KG : 0),
    },
    site,
    epochSeconds: isoToEpochSeconds(mission.launchAt),
    program,
    gravity: 'j2',
    drag: true,
    wind: mission.weather ? windProfileFromWeather(mission.weather) : null,
    solarActivity: mission.weather?.solarActivity ?? 1,
    surfaceTemperatureK: mission.weather?.surfaceTemperatureK ?? 288.15,
    surfacePressurePa: mission.weather?.surfacePressurePa ?? 101_325,
    relativeHumidity: mission.weather?.relativeHumidity ?? 0,
    sampleInterval: 1,
    maxDuration: simulationHorizon,
  };
}

/**
 * Build an end-to-end mission request. The ascent remains a dense numerical
 * integration; Lambert solutions initialise lunar and planetary legs and the
 * worker numerically propagates the configured long-coast perturbation model.
 */
export function buildMissionSimulationOptions(
  input: BuildFlightOptionsInput,
): MissionSimulationOptions {
  const destinationError = missionDestinationError(
    input.mission.missionType,
    input.mission.destinationBody,
  );
  if (destinationError) throw new Error(destinationError);

  return {
    flight: buildFlightOptions(input),
    profile: input.mission.missionType,
    destinationBodyId: input.mission.destinationBody,
    destinationOrbitAltitude:
      input.mission.missionType === 'lunar'
        ? 100_000
        : input.mission.missionType === 'interplanetary'
          ? 300_000
          : undefined,
    coastSamples: 420,
    includeDestinationOrbit: true,
    coastPhysics: {
      model: 'perturbed',
      gravityFidelity: 'j4',
      thirdBodyGravity: true,
      solarRadiationPressure: true,
      atmosphericDrag: true,
      reflectivityCoefficient: 1.3,
      solarActivity: input.mission.weather?.solarActivity ?? 1,
    },
    maneuvers: input.mission.maneuvers.map((maneuver) => ({
      id: maneuver.id,
      name: maneuver.name,
      kind: maneuver.kind,
      startTimeSeconds: maneuver.startTimeSeconds,
      deltaVMetersPerSecond: maneuver.deltaVMetersPerSecond,
      deltaVLvlh: maneuver.deltaVLvlh,
    })),
  };
}

export { toPhysicsVehicle };
